import { useState } from 'react';
import {
  Button,
  Combobox,
  Input,
  Option,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { Person24Regular } from '@fluentui/react-icons';
import { useAssignReferral } from '@/api/hooks/useReferrals';
import { usePeopleSearch } from '@/api/hooks/useGraphUsers';
import { isAuthEnabled } from '@/config/env';
import type { ReferralDto } from '@/types/referral';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  current: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
  },
  fields: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionDetail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  statusText: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

interface AssignmentSectionProps {
  referral: ReferralDto;
  performedBy: string;
  performedByName: string;
}

export function AssignmentSection({
  referral,
  performedBy,
  performedByName,
}: AssignmentSectionProps) {
  const styles = useStyles();
  const assignMutation = useAssignReferral();
  const [email, setEmail] = useState(referral.assignedTo ?? '');
  const [name, setName] = useState(referral.assignedToName ?? '');
  const [searchValue, setSearchValue] = useState('');

  const { data: users, isFetching } = usePeopleSearch(
    isAuthEnabled ? searchValue : '',
  );

  const handleAssign = async () => {
    await assignMutation.mutateAsync({
      id: referral.id,
      data: { assignedTo: email.trim(), assignedToName: name.trim() },
      performedBy,
      performedByName,
    });
  };

  const handleUnassign = async () => {
    await assignMutation.mutateAsync({
      id: referral.id,
      data: { assignedTo: '', assignedToName: '' },
      performedBy,
      performedByName,
    });
    setEmail('');
    setName('');
    setSearchValue('');
  };

  return (
    <div className={styles.root}>
      {referral.assignedTo && (
        <div className={styles.current}>
          <Person24Regular style={{ color: tokens.colorBrandForeground1 }} />
          <Text size={300} weight="semibold">
            {referral.assignedToName}
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            ({referral.assignedTo})
          </Text>
        </div>
      )}

      {isAuthEnabled ? (
        <Combobox
          size="small"
          placeholder="Search by name or email..."
          value={searchValue}
          onInput={(e) => {
            setSearchValue((e.target as HTMLInputElement).value);
          }}
          onOptionSelect={(_e, data) => {
            if (data.optionValue && data.optionText) {
              setEmail(data.optionValue);
              setName(data.optionText);
              setSearchValue(data.optionText);
            }
          }}
          style={{ minWidth: '280px' }}
        >
          {isFetching && (
            <Option key="__loading" value="" text="" disabled>
              <Spinner size="tiny" label="Searching..." />
            </Option>
          )}
          {!isFetching && users && users.length === 0 && searchValue.length >= 2 && (
            <Option key="__empty" value="" text="" disabled>
              <Text className={styles.statusText}>No users found</Text>
            </Option>
          )}
          {users?.map((user) => (
            <Option key={user.mail} value={user.mail} text={user.displayName}>
              <div className={styles.optionContent}>
                <Text weight="semibold" size={300}>
                  {user.displayName}
                </Text>
                <span className={styles.optionDetail}>
                  {user.mail}
                  {user.jobTitle && ` · ${user.jobTitle}`}
                </span>
              </div>
            </Option>
          ))}
        </Combobox>
      ) : (
        <div className={styles.fields}>
          <Input
            size="small"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(_e, d) => setEmail(d.value)}
            style={{ minWidth: '180px', flexGrow: 1 }}
          />
          <Input
            size="small"
            placeholder="Name"
            value={name}
            onChange={(_e, d) => setName(d.value)}
            style={{ minWidth: '140px', flexGrow: 1 }}
          />
        </div>
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          disabled={!email.trim() || !name.trim() || assignMutation.isPending}
          onClick={() => void handleAssign()}
          icon={assignMutation.isPending ? <Spinner size="tiny" /> : undefined}
        >
          Assign
        </Button>
        {referral.assignedTo && (
          <Button
            appearance="secondary"
            size="small"
            disabled={assignMutation.isPending}
            onClick={() => void handleUnassign()}
          >
            Unassign
          </Button>
        )}
      </div>
    </div>
  );
}
